/**
 * Кастомный выпадающий список (вместо нативного select).
 * options: { value, label }[], value, onChange(value), placeholder?, className?
 * searchable?: при true — поле поиска внутри выпадающего списка.
 * Список рендерится в портал; позиция подстраивается под viewport (вверх, если внизу не помещается).
 */
import { useRef, useState, useEffect, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";

const DROPDOWN_MAX_HEIGHT = 220;
const DROPDOWN_GAP = 4;

export function CustomSelect({
  options = [],
  value,
  onChange,
  placeholder = "Выберите…",
  className = "",
  id,
  searchable = false,
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownRect, setDropdownRect] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: DROPDOWN_MAX_HEIGHT,
    openUpward: false,
  });
  const rootRef = useRef(null);
  const searchInputRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));
  const displayLabel = selected ? selected.label : placeholder;

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return options;
    const q = searchQuery.trim().toLowerCase();
    return options.filter((o) => (o.label || "").toLowerCase().includes(q));
  }, [options, searchable, searchQuery]);

  useLayoutEffect(() => {
    if (!open || !rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const spaceBelow = viewportH - rect.bottom - DROPDOWN_GAP;
    const spaceAbove = rect.top - DROPDOWN_GAP;
    const openUpward = spaceBelow < Math.min(spaceAbove, DROPDOWN_MAX_HEIGHT);
    const maxHeight = openUpward
      ? Math.min(DROPDOWN_MAX_HEIGHT, spaceAbove)
      : Math.min(DROPDOWN_MAX_HEIGHT, spaceBelow);

    if (openUpward) {
      setDropdownRect({
        left: rect.left,
        width: rect.width,
        top: rect.top - maxHeight - DROPDOWN_GAP,
        maxHeight,
        openUpward: true,
      });
    } else {
      setDropdownRect({
        top: rect.bottom + DROPDOWN_GAP,
        left: rect.left,
        width: rect.width,
        maxHeight,
        openUpward: false,
      });
    }
  }, [open]);

  useEffect(() => {
    if (open && searchable) {
      setSearchQuery("");
      const t = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        const dropdown = document.querySelector(".customSelectDropdown");
        if (dropdown && dropdown.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener("click", onDoc, true);
    return () => document.removeEventListener("click", onDoc, true);
  }, [open]);

  const dropdownEl = open && (
    <div
      className={`customSelectDropdown ${dropdownRect.openUpward ? "customSelectDropdownOpenUp" : ""}`}
      role="listbox"
      aria-activedescendant={value != null ? String(value) : undefined}
      style={{
        position: "fixed",
        top: dropdownRect.top,
        left: dropdownRect.left,
        width: dropdownRect.width,
        minWidth: 120,
        maxHeight: dropdownRect.maxHeight,
      }}
    >
      {searchable && (
        <div className="customSelectSearchWrap">
          <input
            ref={searchInputRef}
            type="search"
            className="customSelectSearch"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Поиск…"
            aria-label="Поиск"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      <div className="customSelectOptionsWrap">
        {filteredOptions.length === 0 ? (
          <div className="customSelectOption customSelectOptionEmpty">Нет совпадений</div>
        ) : (
          filteredOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="option"
              className={`customSelectOption ${String(opt.value) === String(value) ? "isSelected" : ""}`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          ))
        )}
      </div>
    </div>
  );

  return (
    <>
      <div
        ref={rootRef}
        className={`customSelect ${open ? "isOpen" : ""} ${className}`.trim()}
        id={id}
      >
        <button
          type="button"
          className="customSelectTrigger"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={displayLabel}
        >
          <span className="customSelectValue">{displayLabel}</span>
          <span className="customSelectChevron" aria-hidden="true">▼</span>
        </button>
      </div>
      {dropdownEl && createPortal(dropdownEl, document.body)}
    </>
  );
}
